/**
 * OBS scene poller - keeps `obs/scenes.ts` honest about which scene is live.
 *
 * # Why this is NOT shaped like the other pollers
 *
 * `toggleStatePoll` and `enginesStatePoll` hit a cheap in-memory read on the
 * daemon every few seconds and that is fine. `GET /obs/scenes` is not that
 * call. OPBridge's `opbridge-obs` crate deliberately holds NO persistent
 * link: every read opens a fresh obs-websocket connection, does its
 * Hello/Identify handshake, asks one question and drops the socket. Its own
 * module docs justify that by call frequency - "a scene switch is an
 * infrequent operator action, not a hot path."
 *
 * A 4-second poll would make it a hot path: ~900 handshakes an hour against
 * a machine whose actual job is encoding video, for the whole length of a
 * gig. So this poller is built to ask as rarely as it can while still being
 * useful, on three axes:
 *
 * 1. **Only bridges an OBS control actually points at.** Most rigs have one
 *    bridge with no OBS at all; it should never be asked.
 * 2. **Only while the page is visible.** A backgrounded iPad tab has nobody
 *    reading the lit state, so the handshakes would buy nothing.
 * 3. **Slowly** - {@link POLL_INTERVAL_MS}. This is a correction cadence,
 *    not a live feed: our own switches update the mirror instantly
 *    (commit-on-ack, `recordObsSceneSwitch`), so the poll exists only to
 *    catch scene changes made SOMEWHERE ELSE - in OBS itself, or from a
 *    Stream Deck. Being up to one interval behind on those is the tradeoff,
 *    and it is the right one until OPBridge can push `CurrentProgramScene
 *    Changed` over `/ws/consumer`. That push is the real fix and is filed as
 *    an ask; this is the honest version of what we can do without it.
 */

import { bridges, getConnection, bridgeStateKey } from '../opbridge/store.ts';
import { layoutItems } from '../layout/store.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { recordObsScenes, recordObsFailure, obsState } from './scenes.ts';

/** Correction cadence. Deliberately slow - see the file header. */
const POLL_INTERVAL_MS = 20_000;

/** How long a tick waits when there is nothing to ask. Shorter than the
 *  poll interval so that placing the first OBS button does not leave the
 *  operator staring at an unknown state for 20s. */
const IDLE_INTERVAL_MS = 4_000;

let timer: ReturnType<typeof setTimeout> | null = null;
let stopped = false;

/** Start the poller. Idempotent. */
export function startObsScenePoll(): void {
  if (timer !== null) return;
  stopped = false;
  schedule(0);
}

/** Stop the poller. Used by tests + on tear-down. */
export function stopObsScenePoll(): void {
  stopped = true;
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function schedule(delayMs: number): void {
  if (stopped) return;
  timer = setTimeout(() => {
    timer = null;
    void tick();
  }, delayMs);
}

/**
 * Every bridge id an OBS control on the CURRENT page points at.
 *
 * Current page, not the whole project: a scene button three pages away has
 * nobody looking at it, and asking for it would reintroduce the handshake
 * cost this poller exists to avoid. Switching pages changes `layoutItems()`,
 * so the next tick picks the new page up on its own.
 *
 * A button with no `bridgeIds` contributes NOTHING. That is not an
 * oversight: an OBS button that has not named its bridge is refused at
 * dispatch (`sendObsScene`) and shows "pick a bridge" on its face, so
 * reading the default bridge's OBS here would put a live-looking state under
 * a control that cannot fire - and, worse, would be the WRONG machine's OBS.
 */
export function obsBridgeIdsInUse(): string[] {
  const ids = new Set<string>();
  for (const item of layoutItems()) {
    if (item.kind !== 'button') continue;
    if (item.actionType !== 'obs') continue;
    // Row id, not the stored node id - see `bridgeStateKey`.
    for (const id of item.bridgeIds ?? []) ids.add(bridgeStateKey(id));
  }
  return Array.from(ids);
}

/**
 * Read one bridge's scene list now and record the outcome.
 *
 * Exported because the Inspector calls it directly: opening the panel on an
 * OBS button, or hitting its Refresh, must not wait up to a poll interval
 * for the picker to populate. Never throws - every failure becomes a named
 * status on the bridge (see `obs/scenes.ts`).
 */
export async function refreshObsScenes(bridgeId: string): Promise<void> {
  if (!bridgeId) return;
  const conn = getConnection(bridgeId);
  if (!conn) {
    recordObsFailure(bridgeId, 'error', 'No such bridge.');
    return;
  }
  if (conn.status() !== 'connected') {
    recordObsFailure(bridgeId, 'error', `Bridge is ${conn.status()}.`);
    return;
  }
  try {
    const res = await conn.httpClient().obsScenes();
    recordObsScenes(bridgeId, res.current ?? null, res.scenes ?? []);
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof OpbridgeError ? err.message
              : err instanceof Error           ? err.message
              : String(err);
    // The three causes have three different owners and three different
    // fixes, so they stay apart all the way to the button's face.
    if (status === 401) {
      recordObsFailure(bridgeId, 'unauthorized',
        'OPBridge has not granted OPButtons the /obs/scenes scope.');
    } else if (status === 503) {
      recordObsFailure(bridgeId, 'unavailable',
        'OBS is not running on this machine, or obs-websocket is off.');
    } else if (status === 404) {
      recordObsFailure(bridgeId, 'error',
        'This OPBridge is too old to have the /obs routes.');
    } else {
      recordObsFailure(bridgeId, 'error', msg);
    }
  }
}

/**
 * Ask EVERY connected bridge whether it has OBS, once.
 *
 * This is capability discovery, not state polling, and it is what makes the
 * Computer picker able to say "4 scenes" beside one machine and "no OBS
 * here" beside another. It is deliberately NOT part of the poll loop: the
 * poll asks only about bridges an OBS control already targets, because a
 * bridge with no OBS would otherwise cost a failed obs-websocket dial every
 * 20 seconds forever, for an answer that does not change.
 *
 * Called from the OBS Inspector when it opens, which bounds it to "the
 * operator is looking at the picker right now". Sequential for the same
 * reason the poll is: each call is a full handshake on the far end.
 *
 * Skips bridges already known to be `ok` - their state is the poll's job
 * from then on, and re-dialling a machine we have just read is the waste
 * this whole module is shaped to avoid.
 */
export async function discoverObsBridges(): Promise<void> {
  for (const cfg of bridges()) {
    if (stopped) return;
    const conn = getConnection(cfg.id);
    if (!conn || conn.status() !== 'connected') continue;
    if (obsState(cfg.id).status === 'ok') continue;
    await refreshObsScenes(cfg.id);
  }
}

function pageIsVisible(): boolean {
  // `document` is absent in the pure-logic smokes' Node shim, and a poller
  // that refused to run there would make itself untestable.
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

async function tick(): Promise<void> {
  if (stopped) return;
  if (!pageIsVisible()) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  const wanted = obsBridgeIdsInUse();
  if (wanted.length === 0 || bridges().length === 0) {
    schedule(IDLE_INTERVAL_MS);
    return;
  }
  // Sequential, not Promise.all: each read is a full obs-websocket handshake
  // on the far end, and firing several at once at one machine is exactly the
  // burst this poller is shaped to avoid. There is almost never more than
  // one anyway.
  for (const id of wanted) {
    if (stopped) return;
    await refreshObsScenes(id);
  }
  schedule(POLL_INTERVAL_MS);
}
